import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Center,
  Divider,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { ChevronRightIcon, CheckIcon, WarningIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const ABTestResults = () => {
  const { id } = useParams();
  const [test, setTest] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (id && tenantId) {
      fetchTestResults();
    }
  }, [id, tenantId]);

  const fetchTestResults = async () => {
    try {
      setLoading(true);
      console.log('Fetching test results for ID:', id);
      
      const [testResponse, resultsResponse] = await Promise.all([
        apiClient(`/ab-tests/${tenantId}/${id}`),
        apiClient(`/ab-tests/${tenantId}/${id}/results`)
      ]);
      
      console.log('Test response:', testResponse);
      console.log('Results response:', resultsResponse);
      
      if (testResponse && testResponse.success && testResponse.data) {
        setTest(testResponse.data);
      } else {
        throw new Error('Test not found');
      }
      
      if (resultsResponse && resultsResponse.success && resultsResponse.data) {
        setResults(resultsResponse.data);
      } else {
        // Set empty results if no data available
        setResults({
          impressions_a: 0,
          impressions_b: 0,
          conversions_a: 0,
          conversions_b: 0,
          conversion_rate_a: 0,
          conversion_rate_b: 0,
          improvement: 0,
          statistical_significance: { significant: false, confidence: 0 }
        });
      }
    } catch (error) {
      console.error('Error fetching test results:', error);
      toast({
        title: 'Error loading test results',
        description: error.message || 'Failed to load test results',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/ab-testing');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'green';
      case 'completed': return 'blue';
      case 'paused': return 'orange';
      default: return 'gray';
    }
  };

  const getWinningVariant = () => {
    if (!results) return null;
    
    if (results.conversion_rate_a > results.conversion_rate_b) {
      return 'A';
    } else if (results.conversion_rate_b > results.conversion_rate_a) {
      return 'B';
    }
    return null;
  };

  const formatPercent = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    return parseInt(value || 0).toLocaleString();
  };

  if (loading) {
    return (
      <Container maxW="container.lg" py={6}>
        <Center py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading test results...</Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  const winningVariant = getWinningVariant();
  const hasData = results && (results.impressions_a > 0 || results.impressions_b > 0);

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/ab-testing">A/B Testing</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Test Results</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Box>
            <HStack spacing={3} mb={2}>
              <Heading size="lg">{test?.name}</Heading>
              <Badge colorScheme={getStatusColor(test?.status)}>
                {test?.status}
              </Badge>
            </HStack>
            <Text color="gray.500">
              {test?.description || 'A/B Test Results'}
            </Text>
          </Box>
          <HStack>
            <Button variant="outline" onClick={() => navigate('/ab-testing')}>
              Back to Tests
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => navigate(`/ab-testing/edit/${id}`)}
            >
              Edit Test
            </Button>
          </HStack>
        </HStack>

        {!hasData && (
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>
              This test hasn't collected any data yet. Results will appear once visitors start interacting with your test.
            </AlertDescription>
          </Alert>
        )}

        {hasData && (
          <>
            {/* Summary Cards */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody>
                  <Stat>
                    <StatLabel>Total Impressions</StatLabel>
                    <StatNumber>{formatNumber(results.impressions_a + results.impressions_b)}</StatNumber>
                    <StatHelpText>Combined A + B</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody>
                  <Stat>
                    <StatLabel>Total Conversions</StatLabel>
                    <StatNumber>{formatNumber(results.conversions_a + results.conversions_b)}</StatNumber>
                    <StatHelpText>Combined A + B</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody>
                  <Stat>
                    <StatLabel>Improvement</StatLabel>
                    <StatNumber color={results.improvement > 0 ? 'green.500' : results.improvement < 0 ? 'red.500' : 'gray.500'}>
                      {results.improvement > 0 && '+'}
                      {formatPercent(results.improvement)}
                    </StatNumber>
                    <StatHelpText>
                      {results.improvement > 0 && <StatArrow type="increase" />}
                      {results.improvement < 0 && <StatArrow type="decrease" />}
                      B vs A
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>

              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody>
                  <Stat>
                    <StatLabel>Statistical Significance</StatLabel>
                    <StatNumber color={results.statistical_significance?.significant ? 'green.500' : 'orange.500'}>
                      {results.statistical_significance?.confidence || 0}%
                    </StatNumber>
                    <StatHelpText>
                      <HStack>
                        <Icon 
                          as={results.statistical_significance?.significant ? CheckIcon : WarningIcon} 
                          color={results.statistical_significance?.significant ? 'green.500' : 'orange.500'}
                        />
                        <Text>
                          {results.statistical_significance?.significant ? 'Significant' : 'Not Significant'}
                        </Text>
                      </HStack>
                    </StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Variant Comparison */}
            <Card bg={cardBg} borderColor={borderColor} variant="outline">
              <CardHeader>
                <Heading size="md">Variant Performance</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                  {/* Variant A */}
                  <VStack spacing={4}>
                    <HStack w="full" justify="space-between">
                      <Heading size="sm">Variant A (Control)</Heading>
                      {winningVariant === 'A' && (
                        <Badge colorScheme="green">Winner</Badge>
                      )}
                    </HStack>
                    
                    <VStack spacing={3} w="full">
                      <HStack w="full" justify="space-between">
                        <Text>Impressions:</Text>
                        <Text fontWeight="bold">{formatNumber(results.impressions_a)}</Text>
                      </HStack>
                      <HStack w="full" justify="space-between">
                        <Text>Conversions:</Text>
                        <Text fontWeight="bold">{formatNumber(results.conversions_a)}</Text>
                      </HStack>
                      <HStack w="full" justify="space-between">
                        <Text>Conversion Rate:</Text>
                        <Text fontWeight="bold" color="blue.500">
                          {formatPercent(results.conversion_rate_a)}
                        </Text>
                      </HStack>
                    </VStack>
                    
                    <Box w="full">
                      <Text fontSize="sm" mb={2}>Conversion Rate</Text>
                      <Progress 
                        value={results.conversion_rate_a} 
                        colorScheme="blue" 
                        size="lg"
                        max={Math.max(results.conversion_rate_a, results.conversion_rate_b, 1)}
                      />
                    </Box>
                  </VStack>

                  <Box>
                    <Divider orientation="vertical" display={{ base: 'none', md: 'block' }} />
                    <Divider orientation="horizontal" display={{ base: 'block', md: 'none' }} />
                  </Box>

                  {/* Variant B */}
                  <VStack spacing={4}>
                    <HStack w="full" justify="space-between">
                      <Heading size="sm">Variant B (Treatment)</Heading>
                      {winningVariant === 'B' && (
                        <Badge colorScheme="green">Winner</Badge>
                      )}
                    </HStack>
                    
                    <VStack spacing={3} w="full">
                      <HStack w="full" justify="space-between">
                        <Text>Impressions:</Text>
                        <Text fontWeight="bold">{formatNumber(results.impressions_b)}</Text>
                      </HStack>
                      <HStack w="full" justify="space-between">
                        <Text>Conversions:</Text>
                        <Text fontWeight="bold">{formatNumber(results.conversions_b)}</Text>
                      </HStack>
                      <HStack w="full" justify="space-between">
                        <Text>Conversion Rate:</Text>
                        <Text fontWeight="bold" color="green.500">
                          {formatPercent(results.conversion_rate_b)}
                        </Text>
                      </HStack>
                    </VStack>
                    
                    <Box w="full">
                      <Text fontSize="sm" mb={2}>Conversion Rate</Text>
                      <Progress 
                        value={results.conversion_rate_b} 
                        colorScheme="green" 
                        size="lg"
                        max={Math.max(results.conversion_rate_a, results.conversion_rate_b, 1)}
                      />
                    </Box>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Test Details */}
            <Card bg={cardBg} borderColor={borderColor} variant="outline">
              <CardHeader>
                <Heading size="md">Test Configuration</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <VStack spacing={3} align="start">
                    <Text><strong>Test Type:</strong> {test?.entity_type}</Text>
                    <Text><strong>Goal Metric:</strong> {test?.goal_metric}</Text>
                    <Text><strong>Traffic Split:</strong> {test?.traffic_split}% / {100 - test?.traffic_split}%</Text>
                  </VStack>
                  <VStack spacing={3} align="start">
                    <Text><strong>Started:</strong> {test?.started_at ? new Date(test.started_at).toLocaleDateString() : 'Not started'}</Text>
                    <Text><strong>Status:</strong> {test?.status}</Text>
                    <Text><strong>Duration:</strong> {test?.started_at ? `${Math.ceil((new Date() - new Date(test.started_at)) / (1000 * 60 * 60 * 24))} days` : 'N/A'}</Text>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>
          </>
        )}
      </VStack>
    </Container>
  );
};

export default ABTestResults;